import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["tableBody", "relaxButton", "hustleButton", "proButton"];
  static values = {
    planPrice: { type: Number, default: 899 },
    bonusText: { type: String, default: "bonus" },
    thenText: { type: String, default: "then" },
    recurringText: { type: String, default: "recurring" },
    perMonthText: { type: String, default: "/mo" },
  };

  connect() {
    this.mode = "relax";
    this.showRelaxMode();
  }

  showRelaxMode() {
    this.mode = "relax";
    this.updateButtonStates();
    this.renderTable(1); // Base multiplier
  }

  showHustleMode() {
    this.mode = "hustle";
    this.updateButtonStates();
    this.renderTable(2); // 2x multiplier
  }

  showProMode() {
    this.mode = "pro";
    this.updateButtonStates();
    this.renderTable(4); // 4x multiplier
  }

  updateButtonStates() {
    // Reset all buttons
    this.relaxButtonTarget.classList.remove("bg-yellow-300", "text-black");
    this.relaxButtonTarget.classList.add(
      "bg-gray-800",
      "text-gray-400",
      "hover:bg-gray-700"
    );

    this.hustleButtonTarget.classList.remove("bg-yellow-300", "text-black");
    this.hustleButtonTarget.classList.add(
      "bg-gray-800",
      "text-gray-400",
      "hover:bg-gray-700"
    );

    this.proButtonTarget.classList.remove("bg-yellow-300", "text-black");
    this.proButtonTarget.classList.add(
      "bg-gray-800",
      "text-gray-400",
      "hover:bg-gray-700"
    );

    // Activate current button
    if (this.mode === "relax") {
      this.relaxButtonTarget.classList.remove(
        "bg-gray-800",
        "text-gray-400",
        "hover:bg-gray-700"
      );
      this.relaxButtonTarget.classList.add("bg-yellow-300", "text-black");
    } else if (this.mode === "hustle") {
      this.hustleButtonTarget.classList.remove(
        "bg-gray-800",
        "text-gray-400",
        "hover:bg-gray-700"
      );
      this.hustleButtonTarget.classList.add("bg-yellow-300", "text-black");
    } else if (this.mode === "pro") {
      this.proButtonTarget.classList.remove(
        "bg-gray-800",
        "text-gray-400",
        "hover:bg-gray-700"
      );
      this.proButtonTarget.classList.add("bg-yellow-300", "text-black");
    }
  }

  renderTable(multiplier) {
    const baseClients = [
      { month: 1, newClients: 2, totalClients: 2 },
      { month: 2, newClients: 3, totalClients: 5 },
      { month: 3, newClients: 5, totalClients: 10 },
      { month: 6, newClients: 5, totalClients: 25 },
      { month: 9, newClients: 5, totalClients: 40 },
      { month: 12, newClients: 10, totalClients: 60 },
    ];

    const rows = baseClients
      .map((data) => {
        const totalClients = data.totalClients * multiplier;
        const newClients = data.newClients * multiplier;
        const firstMonthBonus = newClients * this.planPriceValue;
        // This is what they'll earn monthly from ALL clients going forward
        const monthlyRecurring = totalClients * this.planPriceValue * 0.2;
        // For the first month, they only get the bonus
        // For subsequent months, they get bonus from new + recurring from all
        const totalIncome =
          data.month === 1
            ? firstMonthBonus
            : firstMonthBonus +
              (totalClients - newClients) * this.planPriceValue * 0.2;

        const highlight = data.month === 3 || data.month === 12;
        const isLast = data.month === 12;

        let breakdown;
        if (data.month === 1) {
          // First month: only bonuses, but show what recurring will be
          breakdown = `${this.formatCurrency(firstMonthBonus)} ${this.bonusTextValue} (${this.thenTextValue} ${this.formatCurrency(monthlyRecurring)}${this.perMonthTextValue})`;
        } else {
          // Subsequent months: show bonus + what the monthly recurring is now
          breakdown = `${this.formatCurrency(firstMonthBonus)} ${this.bonusTextValue} + ${this.formatCurrency(monthlyRecurring)}${this.perMonthTextValue} ${this.recurringTextValue}`;
        }

        return `
        <tr class="${highlight ? "bg-yellow-900/10" : "bg-black"} transition-colors hover:bg-gray-900/50">
          <td class="px-4 py-3 text-sm ${highlight ? "text-yellow-400 font-semibold" : "text-gray-300"}">
            ${data.month}
          </td>
          <td class="px-4 py-3 text-base ${highlight ? "text-yellow-400 font-semibold" : "text-white font-medium"}">
            ${totalClients}
          </td>
          <td class="px-4 py-3 text-right">
            <div class="${isLast ? "text-xl font-bold text-green-400" : "text-lg font-semibold text-green-400"}">
              ${this.formatCurrency(totalIncome)}
            </div>
            <div class="text-xs text-gray-400 mt-1">
              ${breakdown}
            </div>
          </td>
        </tr>
      `;
      })
      .join("");

    this.tableBodyTarget.innerHTML = rows;
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
}
